import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { message } from 'antd';
import moment from 'moment';
import Layout from '../../components/Layout';
import Loader from '../../components/Loader';
import TrackingModal from '../../components/TrackingModal';
import CheckInModal from './CheckInModal';
import FindMatchModal from './FindMatchModal';
import { get } from '../../services/ApiService';
import usePermissionCheck from '../../hooks/usePermissionCheck';
import './returns.css';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;
const PAGE_SIZES = [10, 20, 50, 100];
const COLUMN_COUNT = 9;

const PORTAL_LABELS = { royal_mail: 'Royal Mail', ebay: 'eBay', amazon: 'Amazon' };

const PORTAL_OPTIONS = [
  { value: '', label: 'All portals' },
  { value: 'royal_mail', label: 'Royal Mail' },
  { value: 'ebay', label: 'eBay' },
  { value: 'amazon', label: 'Amazon' },
];

const STAGES = [
  { value: '', label: 'All', count: 'all' },
  { value: 'no_tracking', label: 'No scan yet', count: 'no_tracking' },
  { value: 'in_transit', label: 'In transit', count: 'in_transit' },
  { value: 'delivered', label: 'Delivered', count: 'delivered' },
  { value: 'awaiting_checkin', label: 'Delivered, not checked in', count: 'awaiting_checkin', alert: true },
  { value: 'to_confirm', label: 'To confirm', count: 'to_confirm' },
  { value: 'closed', label: 'Closed', count: 'closed' },
  { value: 'failed', label: 'Label failed', count: 'failed' },
];

// null marks a gap between page numbers
function pageWindow(current, last) {
  const pages = [];
  for (let page = 1; page <= last; page += 1) {
    if (page === 1 || page === last || Math.abs(page - current) <= 1) pages.push(page);
    else if (pages[pages.length - 1] !== null) pages.push(null);
  }
  return pages;
}

const shortDate = (value) => moment(value).format('D MMM YYYY');
const shortDateTime = (value) => moment(value).format('D MMM YYYY, HH:mm');

function statusOf(record) {
  if (record.status === 'failed') return { label: 'Label failed', tone: 'alert', detail: 'Royal Mail could not create the label' };
  if (record.deliveredAt) return { label: 'Delivered', tone: 'done', detail: `Delivered ${shortDateTime(record.deliveredAt)}` };
  if (record.trackingStatus) return { label: 'In transit', tone: 'moving', detail: record.trackingStatus };
  return { label: 'No scan yet', tone: 'idle', detail: 'No carrier scan or delivery has been reported for this parcel' };
}

const CheckIn = ({ record, onView, onFind }) => {
  const rows = record.checkIns || [];

  if (record.matchSource === 'manual') {
    const stale = rows.length === 0;
    return (
      <button
        type="button"
        className={`returns-status is-button ${stale ? 'is-alert' : 'is-done'}`}
        title={stale ? 'No check-in rows found for this reference' : undefined}
        onClick={() => onView(record)}
      >
        Matched <i className="fas fa-eye" aria-hidden="true"></i>
        <span className="visually-hidden">, view details</span>
      </button>
    );
  }

  if (rows.length > 0) {
    return (
      <button type="button" className="returns-status is-done is-button" onClick={() => onView(record)}>
        Checked in <i className="fas fa-eye" aria-hidden="true"></i>
        <span className="visually-hidden">, view details</span>
      </button>
    );
  }

  // In transit will catch up on its own; a failed label has nothing to match.
  const canFind = record.status !== 'failed' && (Boolean(record.deliveredAt) || !record.trackingStatus);

  if (record.deliveredAt) {
    if (!canFind) return <span className="returns-status is-alert">Not checked in</span>;
    return (
      <button type="button" className="returns-status is-alert is-button" onClick={() => onFind(record)}>
        Not checked in <i className="fas fa-search" aria-hidden="true"></i>
        <span className="visually-hidden">, find a check-in</span>
      </button>
    );
  }

  if (!canFind) return null;
  return (
    <button type="button" className="returns-track" onClick={() => onFind(record)}>
      Find match
    </button>
  );
};

const ReviewBadge = ({ record }) => {
  if (!record.matchSource) return <span className="returns-dash" aria-label="No review">—</span>;
  if (record.review?.closed) {
    return (
      <span className="returns-status is-done" title={`Closed by ${record.review.closedBy || 'unknown'}`}>
        Closed
      </span>
    );
  }
  return <span className="returns-status is-open">Open</span>;
};

function canTrackRecord(record) {
  if (!record.trackingNumber) return false;
  if (record.channel === 'ebay') return Boolean(record.carrier);
  if (record.channel === 'amazon') return record.carrier === 'Royal Mail';
  return record.status === 'created' && Boolean(record.trackingStatus);
}

const trackingEndpoint = (record) =>
  record.channel === 'ebay'
    ? `api/returns/ebay/${encodeURIComponent(record.externalReturnId)}/tracking`
    : `api/tracking/single/${encodeURIComponent(record.trackingNumber)}`;

const ReturnsList = () => {
  const [returns, setReturns] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState({ page: 1, limit: PAGE_SIZE, search: '', state: '', channel: '' });
  const [tracking, setTracking] = useState({ open: false, loading: false, data: null, orderInfo: null });
  const [checkInView, setCheckInView] = useState(null);
  const [findMatchFor, setFindMatchFor] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const canTrack = usePermissionCheck(['order.tracker']);
  const refresh = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      setQuery((q) => (q.search === search ? q : { ...q, search, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchReturns = async () => {
      setLoading(true);
      try {
        const res = await get(`api/returns?${new URLSearchParams(query)}`);
        if (cancelled) return;
        setReturns(res.returns || []);
        setTotal(res.total || 0);
      } catch (error) {
        if (!cancelled && !error?.handledByInterceptor) message.error('Could not load returns. Reload to try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReturns();
    return () => { cancelled = true; };
  }, [query, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    get(`api/returns/summary?${new URLSearchParams({ channel: query.channel })}`)
      .then((res) => { if (!cancelled) setSummary(res.summary || null); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [query.channel, reloadKey]);

  const openTracking = async (record) => {
    const orderInfo = { orderId: record.orderRef };
    setTracking({ open: true, loading: true, data: null, orderInfo });
    try {
      const res = await get(trackingEndpoint(record));
      setTracking({ open: true, loading: false, data: res.success ? res.data : null, orderInfo });
    } catch (error) {
      setTracking({ open: true, loading: false, data: null, orderInfo });
      if (error?.response?.status === 429) message.warning('Royal Mail tracking limit reached. Try again later.');
    }
  };

  const totalPages = Math.max(Math.ceil(total / query.limit), 1);
  const firstRow = total === 0 ? 0 : (query.page - 1) * query.limit + 1;
  const lastRow = Math.min(query.page * query.limit, total);
  const goToPage = (page) => setQuery((q) => ({ ...q, page }));
  const filtered = query.search || query.state || query.channel;

  const renderRows = () => {
    if (loading) {
      return <tr><td colSpan={COLUMN_COUNT} className="returns-empty"><Loader /></td></tr>;
    }
    if (returns.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT} className="returns-empty">
            {filtered ? 'No returns match these filters.' : 'No returns yet. They appear here once a QR return is generated or a marketplace reports one.'}
          </td>
        </tr>
      );
    }
    return returns.map((record) => {
      const status = statusOf(record);
      return (
        <tr key={record.returnId}>
          <td>
            <span className={`returns-portal is-${record.channel}`}>{PORTAL_LABELS[record.channel] || record.channel}</span>
          </td>
          <td className="returns-order">
            {record.orderId ? <Link to={`/orders/${record.orderId}`}>{record.orderRef}</Link> : record.orderRef || 'No order linked'}
          </td>
          <td className="returns-nowrap">{record.trackingNumber || <span className="returns-muted">None</span>}</td>
          <td>
            <div className="returns-clip" title={record.notes || record.reason || ''}>
              {record.reason || <span className="returns-dash" aria-label="No reason recorded">—</span>}
            </div>
          </td>
          <td>
            <span className={`returns-status is-${status.tone}`} title={status.detail}>{status.label}</span>
          </td>
          <td><CheckIn record={record} onView={setCheckInView} onFind={setFindMatchFor} /></td>
          <td><ReviewBadge record={record} /></td>
          <td className="returns-nowrap" title={record.createdBy ? `Created by ${record.createdBy}` : undefined}>
            {shortDate(record.createdAt)}
          </td>
          <td>
            {canTrack && canTrackRecord(record) && (
              <button type="button" className="returns-track" onClick={() => openTracking(record)}>
                Track
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <Layout>
      <div className="returns-page">
        <div className="returns-head">
          <h2>Returns</h2>
          <span>{loading ? '' : `${total} shown`}</span>
        </div>

        <div className="returns-stages" role="group" aria-label="Filter by stage">
          {STAGES.map((stage) => (
            <button
              key={stage.value}
              type="button"
              aria-pressed={query.state === stage.value}
              className={`returns-stage ${query.state === stage.value ? 'is-active' : ''} ${stage.alert && summary?.[stage.count] ? 'is-alert' : ''}`}
              onClick={() => setQuery((q) => ({ ...q, state: stage.value, page: 1 }))}
            >
              <strong>{summary ? summary[stage.count] : '–'}</strong>
              {stage.label}
            </button>
          ))}
        </div>

        <div className="returns-tools">
          <div className="returns-search">
            <i className="fas fa-search"></i>
            <input
              type="search"
              placeholder="Search order, tracking or return ID"
              aria-label="Search order, tracking or return ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="returns-filters">
            <select
              className="returns-select"
              aria-label="Filter by portal"
              value={query.channel}
              onChange={(e) => setQuery((q) => ({ ...q, channel: e.target.value, page: 1 }))}
            >
              {PORTAL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <label htmlFor="returns-page-size">Rows per page</label>
            <select
              id="returns-page-size"
              className="returns-select is-narrow"
              value={query.limit}
              onChange={(e) => setQuery((q) => ({ ...q, limit: Number(e.target.value), page: 1 }))}
            >
              {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
        </div>

        <div className="returns-card">
          <table className="returns-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Portal</th>
                <th>Order</th>
                <th>Tracking</th>
                <th>Reason</th>
                <th>Delivery</th>
                <th>Checked in</th>
                <th>Review</th>
                <th>Opened</th>
                <th style={{ width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </table>

          <div className="returns-foot">
            <span>{loading ? 'Loading…' : `Showing ${firstRow}–${lastRow} of ${total}`}</span>
            <nav className="returns-pager" aria-label="Pages">
              <button type="button" className="returns-page-btn" disabled={query.page <= 1} onClick={() => goToPage(query.page - 1)}>
                Previous
              </button>
              {pageWindow(query.page, totalPages).map((page, index) =>
                page === null ? (
                  <span key={`gap-${index}`} aria-hidden="true">…</span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    aria-current={page === query.page ? 'page' : undefined}
                    className={`returns-page-btn ${page === query.page ? 'is-current' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button type="button" className="returns-page-btn" disabled={query.page >= totalPages} onClick={() => goToPage(query.page + 1)}>
                Next
              </button>
            </nav>
          </div>
        </div>

        <CheckInModal record={checkInView} onClose={() => setCheckInView(null)} onChanged={refresh} />
        <FindMatchModal record={findMatchFor} onClose={() => setFindMatchFor(null)} onLinked={refresh} />
        <TrackingModal
          open={tracking.open}
          onCancel={() => setTracking((t) => ({ ...t, open: false }))}
          loading={tracking.loading}
          data={tracking.data}
          orderInfo={tracking.orderInfo}
        />
      </div>
    </Layout>
  );
};

export default ReturnsList;
